import React, { useState } from 'react';
import { AlertTriangleIcon, FileTextIcon, Loader2Icon, LockIcon, PhoneIcon, CreditCardIcon, LandmarkIcon } from 'lucide-react';
import { STEP_ESTIMATE, useFunnel } from '../../contexts/FunnelContext';
import { phones, siteConfig, isLive } from '../../data/siteConfig';
import type { StringKey } from '../../data/i18n';
import type { StepErrors } from '../../utils/validation';
import { stepErrors } from '../../utils/validation';
import { usd } from '../../utils/format';
import { fireAddToCart, fireInitiateCheckout, moveContentId } from '../../utils/metaPixel';
import { ownershipLine } from '../../data/business';
import { LegalLinks } from '../legal/LegalLinks';
import { ChoiceCard } from './ChoiceCard';
import { TextField } from './TextField';
import { HelpAnswers } from './HelpAnswers';
interface DocRow {
  key: StringKey;
  who: string;
  when: StringKey;
}
export function StepReserve() {
  const {
    t,
    lang,
    data,
    update,
    quote,
    estimateAccepted,
    goTo,
    submit
  } = useFunnel();
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [errors, setErrors] = useState<StepErrors>({});
  const checkoutReady = isLive() && siteConfig.depositCheckoutEnabled;
  if (!estimateAccepted) {
    return <div className="rounded-2xl border-2 border-magenta/40 bg-white p-6" data-id="element-873" data-mp="16vpik80oe4ke">
        <AlertTriangleIcon className="h-8 w-8 text-magenta" aria-hidden="true" data-id="element-874" data-mp="16tmhmsxzir0w" />
        <h1 className="mt-3 font-display text-2xl font-black text-ink" data-id="element-875" data-mp="24ixy7mdzwjeh">{t('staleTitle')}</h1>
        <p className="mt-2 text-[15px] text-ink-600" data-id="element-876" data-mp="1zlob4de2wjtt">{t('staleBody')}</p>
        <button type="button" onClick={() => goTo(STEP_ESTIMATE)} className="mt-5 h-12 rounded-xl bg-ink px-5 font-semibold text-white hover:bg-ink-700" data-id="element-877" data-mp="1ukihmdclgsj8">
          {t('staleBtn')}
        </button>
      </div>;
  }
  const docs: DocRow[] = [{
    key: 'docBooking',
    who: t('whoYou'),
    when: 'whenNow'
  }, ...(data.paymentMethod === 'card' ? [data.thirdPartyPayer ? {
    key: 'docThird' as StringKey,
    who: `${t('whoPayer')}${data.payerName ? `: ${data.payerName}` : ''}`,
    when: 'whenNow' as StringKey
  } : {
    key: 'docDeposit' as StringKey,
    who: t('whoCardholder'),
    when: 'whenNow' as StringKey
  }] : []), {
    key: 'docFull',
    who: data.thirdPartyPayer ? t('whoPayer') : t('whoCardholder'),
    when: 'whenLater'
  }, {
    key: 'docBol',
    who: t('whoYou'),
    when: 'whenLater'
  }];
  const goShopify = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const url = siteConfig.shopifyCheckoutUrl;
    if (!url) return;
    e.preventDefault();
    const params = {
      content_ids: [moveContentId(data.bedrooms)],
      content_category: data.homeType ?? 'unspecified',
      crew: quote.crew.used,
      packing: data.packing ?? 'unspecified',
      service_area: quote.zoneReasonKey
    };
    fireAddToCart(quote.deposit, `atc_${quote.hash}`, params);
    fireInitiateCheckout(quote.deposit, `ic_${quote.hash}`, {
      ...params,
      estimated_move_value: quote.laborHigh ?? 0
    });
    // Short delay so the pixel request leaves before the page navigates away.
    window.setTimeout(() => window.location.assign(url), 300);
  };
  const send = async () => {
    const e = stepErrors(8, data);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSending(true);
    setFailed(false);
    try {
      await submit();
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };
  return <div data-id="element-878" data-mp="3fdcvd5kizn9y">
      <div className="mb-7" data-id="element-879" data-mp="3fd35dhzhdg49">
        <h1 className="font-display text-3xl font-black leading-tight text-magenta sm:text-4xl" data-id="element-880" data-mp="3fcbicvjy8qpl">{t('reserveTitle')}</h1>
        <p className="mt-2 text-base text-ink-600" data-id="element-881" data-mp="3acp5in819chb">{t('reserveIntro')}</p>
        <p className="mt-2 font-mono text-xs text-ink-500" data-id="element-882" data-mp="3o5oo4y343mt3">
          {t('versionLabel')}: {quote.configVersion} · #{quote.hash}
        </p>
      </div>

      <section className="grid gap-6 md:grid-cols-2" data-id="element-883" data-mp="1yqapowsrx5e">
        <fieldset data-id="element-884" data-mp="1z9iwdwnb768">
          <legend className="mb-2 font-display text-lg font-extrabold text-ink" data-id="element-885" data-mp="1wncihlhqxpl">{t('payerTitle')}</legend>
          <div role="radiogroup" aria-label={t('payerTitle')} className="grid gap-2" data-id="element-886" data-mp="h1zvvnfetyya">
            <ChoiceCard size="sm" selected={!data.thirdPartyPayer} onSelect={() => update({
            thirdPartyPayer: false
          })} title={t('payerMe')} data-id="element-887" data-mp="h06yyzulmt3h" />
            <ChoiceCard size="sm" selected={data.thirdPartyPayer} onSelect={() => update({
            thirdPartyPayer: true
          })} title={t('payerOther')} data-id="element-888" data-mp="pu1rn8ejar28" />
          </div>
          {data.thirdPartyPayer && <div className="mt-4 space-y-3" data-id="element-889" data-mp="3u9bcyp7ur8zs">
              <TextField label={t('payerName')} value={data.payerName} onChange={v => update({
            payerName: v
          })} data-id="element-890" data-mp="3uacsb5vwihnb" />
              <TextField label={t('payerEmail')} type="email" value={data.payerEmail} onChange={v => update({
            payerEmail: v
          })} data-id="element-891" data-mp="26k7nz1igx9t" />
              <TextField label={t('payerPhone')} type="tel" value={data.payerPhone} onChange={v => update({
            payerPhone: v
          })} data-id="element-892" data-mp="c3gapp8n4r6z" />
              {errors.payer && <p className="text-sm font-medium text-magenta-dark" data-id="element-893" data-mp="1iiqzrre0bwnh">{t(errors.payer)}</p>}
            </div>}
        </fieldset>
        <fieldset data-id="element-894" data-mp="37zuvna6yt9oa">
          <legend className="mb-2 font-display text-lg font-extrabold text-ink" data-id="element-895" data-mp="380e13rffi5lf">{t('methodTitle')}</legend>
          <div role="radiogroup" aria-label={t('methodTitle')} className="grid gap-2" data-id="element-896" data-mp="3322s1iwyjz49">
            <ChoiceCard size="sm" selected={data.paymentMethod === 'card'} onSelect={() => update({
            paymentMethod: 'card'
          })} title={t('payCard')} icon={<CreditCardIcon className="h-5 w-5" data-id="element-898" data-mp="2taycoi3dec8w" />} data-id="element-897" data-mp="2y9jegly9t3zg" />
            <ChoiceCard size="sm" selected={data.paymentMethod === 'zelle'} onSelect={() => update({
            paymentMethod: 'zelle'
          })} title={t('payZelle')} icon={<LandmarkIcon className="h-5 w-5" data-id="element-900" data-mp="2k98mffl85jcp" />} data-id="element-899" data-mp="2pa65due26hf9" />
          </div>
          {data.paymentMethod === 'zelle' && <p className="mt-3 text-sm leading-relaxed text-ink-600" data-id="element-901" data-mp="2fbpaimc01inx">{t('zelleNote')}</p>}
        </fieldset>
      </section>

      <section className="mt-10" data-id="element-902" data-mp="2aeqhk742nhrb">
        <h2 className="font-display text-lg font-extrabold text-ink" data-id="element-903" data-mp="dkc57w64d8rc">{t('docsTitle')}</h2>
        <ul className="mt-3 divide-y divide-ink-200 rounded-2xl bg-white ring-1 ring-ink-200" data-id="element-904" data-mp="8khg9y5wmt8x">
          {docs.map(d => <li key={d.key} className="flex items-start gap-3 p-4" data-id="element-905" data-mp="230fsmc9cxiqo">
              <FileTextIcon className="mt-0.5 h-5 w-5 shrink-0 text-ink-400" aria-hidden="true" data-id="element-906" data-mp="27sfz6njxn9w8" />
              <div className="min-w-0 flex-1" data-id="element-907" data-mp="1t3ju8zd6aczu">
                <p className="font-semibold text-ink" data-id="element-908" data-mp="1t4mh8w7djvah">{t(d.key)}</p>
                <p className="text-sm text-ink-500" data-id="element-909" data-mp="1y1cvrf3j6its">
                  {d.who} · {t(d.when)}
                </p>
              </div>
              <span className="text-right text-xs font-medium text-ink-500" data-id="element-910" data-mp="2lsidjzt17l6q">{t('docStatusNot')}</span>
            </li>)}
        </ul>
      </section>

      <section className="mt-8 rounded-xl bg-ink-100 p-5" data-id="element-911" data-mp="2crsl962zccxr">
        <h2 className="font-semibold text-ink" data-id="element-912" data-mp="2ctdfsvpanvvm">{t('cancelTitle')}</h2>
        <p className="mt-1 text-[15px] leading-relaxed text-ink-700" data-id="element-913" data-mp="2gr1dg6idg2r7">{t('cancelBody')}</p>
        <p className="mt-3 text-[15px] font-semibold text-ink" data-id="element-914" data-mp="11b2mg80vyne6">
          {t('lineDeposit')}: {usd(quote.deposit, lang)}. <span className="font-normal text-ink-600" data-id="element-915" data-mp="15f0ksgidzu66">{t('depositCredit')}</span>
        </p>
      </section>

      <section className="mt-8 rounded-2xl border-2 border-ink bg-white p-5 sm:p-6" data-id="element-916" data-mp="xs3j1rg69f3z">
        <div className="grid gap-3 sm:grid-cols-2" data-id="element-917" data-mp="subpkszvpkxo">
          {[t('reserve99'), t('prepay')].map(label => <button key={label} type="button" disabled={!checkoutReady} className="flex min-h-[56px] flex-col items-center justify-center rounded-xl border-2 border-ink-200 px-4 text-ink disabled:cursor-not-allowed disabled:bg-ink-100" data-id="element-918" data-mp="17rchoxlzfhwo">
              <span className="flex items-center gap-2 font-bold" data-id="element-919" data-mp="a4lyafvbrhai">
                <LockIcon className="h-4 w-4 text-ink-400" aria-hidden="true" data-id="element-920" data-mp="a296zc3ofuks" />
                {label}
              </span>
              {!checkoutReady && <span className="text-xs font-medium text-ink-500" data-id="element-921" data-mp="k2047ljmgnok">{t('notAvailable')}</span>}
            </button>)}
        </div>
        {siteConfig.shopifyCheckoutUrl && <a href={siteConfig.shopifyCheckoutUrl} onClick={goShopify} className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[17px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-ink-700 active:scale-[0.98]" data-id="element-922" data-mp="3nji7dfog96in">
            <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-923" data-mp="3bxbp2g1008pg" />
            {lang === 'es' ? `Pagar depósito de ${usd(quote.deposit, lang)} en Shopify` : `Pay ${usd(quote.deposit, lang)} deposit on Shopify`}
          </a>}
        {failed && <p role="alert" className="mt-4 rounded-lg bg-magenta-soft p-3 text-sm font-medium text-ink" data-id="element-924" data-mp="3pxw4d27pfme7">
            {t('submitError')}
          </p>}
        <button type="button" onClick={send} disabled={sending} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-magenta text-[19px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-magenta-dark active:scale-[0.98] disabled:opacity-70" data-id="element-925" data-mp="3up4gw74bznvl">
          {sending && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-926" data-mp="1x23aa0xhwsb4" />}
          {sending ? t('submitting') : failed ? t('retry') : t('requestEstimate')}
        </button>
        <p className="mt-3 text-sm text-ink-600" data-id="element-927" data-mp="1nzu4fod69n42">{t('requestNote')}</p>
        <div className="mt-3 border-t border-ink-200 pt-3 text-sm text-ink-600" data-id="element-928" data-mp="1o26t0kdfqvq3">
          <p data-id="element-929" data-mp="1j3c20v5be0uw">{ownershipLine[lang]}</p>
          <p className="mt-1" data-id="element-930" data-mp="2fr9wvz1eu427">
            {lang === 'es' ? 'Mensajes de texto: ' : 'Text messages: '}
            {data.consentSmsMarketing || data.consentSmsNonMarketing ? [data.consentSmsNonMarketing && (lang === 'es' ? 'sin fines de marketing' : 'non-marketing'), data.consentSmsMarketing && 'marketing'].filter(Boolean).join(' + ') : lang === 'es' ? 'no aceptados' : 'not accepted'}
          </p>
          <LegalLinks lang={lang} newTab className="text-ink-700" data-id="element-931" data-mp="2zhlpzo5n9nl7" />
        </div>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm" data-id="element-932" data-mp="2un7sng7f96dw">
          <a href={phones.ai.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-ink underline underline-offset-4" data-id="element-933" data-mp="1ot01ot1qnfvm">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-934" data-mp="1jwt53qv8fgqc" />
            {t('callKyle')}: {phones.ai.display}
          </a>
          <a href={phones.human.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-ink underline underline-offset-4" data-id="element-935" data-mp="1tt4heguto6ks">
            {t('humanHelp')}: {phones.human.display}
          </a>
        </div>
      </section>

      <HelpAnswers screen="stepReserve" className="mt-10" data-id="element-936" data-mp="22srl59b0xoh0" />
    </div>;
}